import { Paper, Stack, Typography } from '@mui/material';
import { INTEGRATION_TYPES } from '@vy/integrations-types';
import type { CatalogProducts } from '@vy/types';
import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';
import { useCatalogProductsList } from '../../../../hooks/requests/use-catalog-products-list';
import {
  CatalogProductsFormSchema,
  type CatalogProductsFormData,
} from '../../ProductsPage/products-page-components/CreateProductDialog/create-product-dialog-types/catalog-products-form-schema';
import { NetSuiteProductsDropdown } from '../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteProductsDropdown';
import {
  AnrokProductInput,
  NetSuiteClassDropdownForCatalog,
  QuickBooksProductsDropdownForCatalog,
  XeroProductsDropdownForCatalog,
} from '../../ProductsPage/products-page-components/CreateProductDialog/UpsertCatalogProductsDialog/components';
import { findIntegrationByProviderInEntities } from '@vy/services';
import type { NetSuiteProductEntity, AnrokProductEntity, QuickBooksProduct, XeroProduct } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { useQuickBooksProducts, useXeroProducts } from '../../../../hooks/requests/integrations';
import { buildQuickBooksEntityFromExternalIntegration, buildXeroEntityFromExternalIntegration } from '../../ProductsPage/products-page-components/CreateProductDialog/UpsertCatalogProductsDialog/hooks/build-erp-entity-from-external-integration';
import {
  buildNetSuiteIntegrationEntities,
  buildQuickBooksIntegrationEntities,
  buildXeroIntegrationEntities,
  buildAnrokIntegrationEntities,
} from '../../ProductsPage/products-page-components/CreateProductDialog/UpsertCatalogProductsDialog/hooks/build-integration-entities';
import type { ProductIntegrationEntity } from '@vy/types';

export type MeterErpSectionHandle = {
  getIntegrationEntities: () => ProductIntegrationEntity[];
};

type MeterErpSectionProps = {
  meterName: string;
};

export const MeterErpSection = forwardRef<MeterErpSectionHandle, MeterErpSectionProps>(
  ({ meterName }, ref) => {
    const isNetSuiteConnected = useActivatedIntegration(INTEGRATION_TYPES.NETSUITE);
    const isQuickBooksConnected = useActivatedIntegration(INTEGRATION_TYPES.QUICKBOOKS);
    const isXeroConnected = useActivatedIntegration(INTEGRATION_TYPES.XERO);
    const isAnrokConnected = useActivatedIntegration(INTEGRATION_TYPES.ANROK);

    const hasAnyIntegration = isNetSuiteConnected || isQuickBooksConnected || isXeroConnected || isAnrokConnected;

    const catalogProductsQuery = useCatalogProductsList({ enabled: hasAnyIntegration });
    const quickBooksProducts = useQuickBooksProducts();
    const xeroProducts = useXeroProducts();

    const companionCatalogProduct = catalogProductsQuery.data?.catalogProducts?.find(
      (catalogProduct: CatalogProducts) => catalogProduct.name === meterName,
    );

    const { control, getValues, reset } = useForm<CatalogProductsFormData>({
      resolver: zodResolver(CatalogProductsFormSchema),
      mode: 'onChange',
      defaultValues: { name: meterName },
    });

    useEffect(() => {
      if (isNotNullOrUndefined(companionCatalogProduct)) {
        reset(buildDefaultValues(
          companionCatalogProduct,
          meterName,
          quickBooksProducts.data?.products ?? [],
          xeroProducts.data?.products ?? [],
        ));
      }
    }, [companionCatalogProduct, meterName, reset, quickBooksProducts.data, xeroProducts.data]);

    useImperativeHandle(ref, () => ({
      getIntegrationEntities: () => {
        const erpFormValues = getValues();
        return [
          ...buildNetSuiteIntegrationEntities(erpFormValues.netSuiteProduct, erpFormValues.netSuiteClass),
          ...buildQuickBooksIntegrationEntities(erpFormValues.quickBooksProduct),
          ...buildXeroIntegrationEntities(erpFormValues.xeroProduct),
          ...buildAnrokIntegrationEntities(erpFormValues.anrokProduct),
        ];
      },
    }));

    if (!hasAnyIntegration) {
      return null;
    }

    return (
      <Paper
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          padding: '24px',
          borderRadius: '14px',
        })}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">ERP Integration</Typography>

          {isNetSuiteConnected && (
            <>
              <NetSuiteProductsDropdown
                control={control}
                fieldPath="netSuiteProduct"
                showDropdown={true}
                placeholder="Select NetSuite product"
              />
              <NetSuiteClassDropdownForCatalog control={control} />
            </>
          )}

          {isQuickBooksConnected && <QuickBooksProductsDropdownForCatalog control={control} />}

          {isXeroConnected && <XeroProductsDropdownForCatalog control={control} />}

          {isAnrokConnected && <AnrokProductInput control={control} />}
        </Stack>
      </Paper>
    );
  },
);

MeterErpSection.displayName = 'MeterErpSection';

function buildDefaultValues(
  catalogProduct: CatalogProducts,
  meterName: string,
  quickBooksProducts: QuickBooksProduct[],
  xeroProducts: XeroProduct[],
): CatalogProductsFormData {
  const netSuiteEntity = findIntegrationByProviderInEntities<NetSuiteProductEntity>(
    catalogProduct.integrationEntities ?? [],
    INTEGRATION_TYPES.NETSUITE,
  );

  const quickBooksEntity = buildQuickBooksEntityFromExternalIntegration(catalogProduct.externalIntegration, quickBooksProducts);
  const xeroEntity = buildXeroEntityFromExternalIntegration(catalogProduct.externalIntegration, xeroProducts);

  const anrokEntity = findIntegrationByProviderInEntities<AnrokProductEntity>(
    catalogProduct.integrationEntities ?? [],
    INTEGRATION_TYPES.ANROK,
  );

  return {
    name: meterName,
    netSuiteProduct: isNotNullOrUndefined(netSuiteEntity) ? [netSuiteEntity] : [],
    netSuiteClass: netSuiteEntity?.metadata?.classMetadata ?? undefined,
    quickBooksProduct: isNotNullOrUndefined(quickBooksEntity) ? [quickBooksEntity] : [],
    xeroProduct: isNotNullOrUndefined(xeroEntity) ? [xeroEntity] : [],
    anrokProduct: isNotNullOrUndefined(anrokEntity) ? [anrokEntity] : [],
  };
}

